import { GradientParser } from '../../lib/gradient';

/**
 * Check if fill is a gradient pattern (gradient + background-size)
 * Format: gradient-pattern(gradient, width, height, widthUnit, heightUnit)
 * @param {string} fill - The fill value to check
 * @returns {boolean} - True if the fill is a gradient pattern
 */
export function isGradientPatternFill(fill) {
  if (typeof fill !== 'string') return false;
  return fill.trim().startsWith('gradient-pattern(');
}

/**
 * Parse gradient pattern fill string
 * @param {string} fill - The gradient pattern fill string
 * @returns {Object|null} - Parsed pattern info { gradient, patternWidth, patternHeight, widthUnit, heightUnit } or null
 */
export function parseGradientPatternFill(fill) {
  if (!isGradientPatternFill(fill)) return null;

  const trimmed = fill.trim();
  // Remove 'gradient-pattern(' prefix and ')' suffix
  const inner = trimmed.slice('gradient-pattern('.length, -1);

  // Find the last 4 comma-separated values (width, height, widthUnit, heightUnit)
  // The gradient itself may contain commas, so we need to parse from the end
  const parts = inner.split(',');
  if (parts.length < 5) return null;

  const heightUnit = parts.pop().trim();
  const widthUnit = parts.pop().trim();
  const patternHeight = parseFloat(parts.pop().trim());
  const patternWidth = parseFloat(parts.pop().trim());
  const gradient = parts.join(',').trim();

  return {
    gradient,
    patternWidth,
    patternHeight,
    widthUnit,
    heightUnit,
  };
}

/**
 * Create a CanvasPattern from a gradient-pattern fill
 * @param {CanvasRenderingContext2D} context - The canvas context to create pattern for
 * @param {string} fill - The gradient-pattern fill string
 * @param {Object} size - The size of the element { w, h }
 * @returns {CanvasPattern|null} - The created pattern or null
 */
export function createGradientPattern(context, fill, size) {
  const patternInfo = parseGradientPatternFill(fill);
  if (!patternInfo) return null;

  const { gradient, patternWidth, patternHeight, widthUnit, heightUnit } = patternInfo;

  // Calculate actual pattern size in pixels
  let pWidth = patternWidth;
  let pHeight = patternHeight;
  if (widthUnit === '%') {
    pWidth = (patternWidth / 100) * Math.abs(size.w);
  }
  if (heightUnit === '%') {
    pHeight = (patternHeight / 100) * Math.abs(size.h);
  }

  // Ensure minimum size of 1px
  pWidth = Math.max(1, Math.round(pWidth));
  pHeight = Math.max(1, Math.round(pHeight));

  // Create an offscreen canvas for the pattern tile
  const patternCanvas = document.createElement('canvas');
  patternCanvas.width = pWidth;
  patternCanvas.height = pHeight;
  const patternCtx = patternCanvas.getContext('2d');

  // Render the gradient onto the pattern canvas
  const patternGradient = GradientParser.create(patternCtx, gradient, {
    x: 0,
    y: 0,
    w: pWidth,
    h: pHeight,
  });

  if (patternGradient) {
    patternCtx.fillStyle = patternGradient;
    patternCtx.fillRect(0, 0, pWidth, pHeight);
  }

  // Create a repeating pattern from the gradient tile
  return context.createPattern(patternCanvas, 'repeat');
}
